import {
  Badge,
  Button,
  Card,
  Group,
  JsonInput,
  NumberInput,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import type { AdminConfigItem } from '@prepzio/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { InfoTip } from '../components/InfoTip';
import { useAppConfig, useUpdateConfig } from '../hooks/use-content';
import { useT, type TranslateFn } from '../i18n';
import { errorMessage } from '../lib/ui';

/** "0 – 1" / "0 – 23 · integer" — the allowed range, spelled out. */
function rangeLabel(item: AdminConfigItem, t: TranslateFn): string {
  const bounds =
    item.min !== null && item.max !== null
      ? `${String(item.min)} – ${String(item.max)}`
      : item.min !== null
        ? `≥ ${String(item.min)}`
        : item.max !== null
          ? `≤ ${String(item.max)}`
          : t('config.unbounded');
  return item.integer ? `${bounds} · ${t('config.integer')}` : bounds;
}

/** One editable config row: default vs effective, a type-specific editor, save/reset. */
function ConfigRow({ item, highlight }: { item: AdminConfigItem; highlight: boolean }) {
  const t = useT();
  const update = useUpdateConfig();
  const [draft, setDraft] = useState<unknown>(item.effective);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Arriving from a business rule: the knob the rule names has to be findable
  // without reading every group. A rule saying "tunable" and naming a key, with
  // no way to reach it, is a pointer to nowhere.
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlight]);

  const overridden = item.override !== null;
  const dirty = JSON.stringify(draft) !== JSON.stringify(item.effective);

  function save(value: unknown) {
    setErr(null);
    update.mutate({ key: item.key, value }, { onError: (e) => setErr(errorMessage(e)) });
  }

  return (
    <Card
      ref={ref}
      withBorder
      padding="md"
      radius="sm"
      style={
        highlight
          ? { borderColor: 'var(--mantine-color-blue-5)', borderWidth: 2, scrollMarginTop: 80 }
          : undefined
      }
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
        <div style={{ minWidth: 0, flex: 1 }}>
          <Group gap={6} wrap="wrap">
            <Text fw={600} size="sm" ff="monospace">
              {item.key}
            </Text>
            {/* The other direction: a knob is only meaningful next to the rule it
                serves, and "what does this number decide?" is the question you
                arrive at this screen with. */}
            {item.br.map((b) => (
              <Badge
                key={b}
                component={Link}
                to={`/business-rules?q=${encodeURIComponent(b)}`}
                variant="light"
                color="grape"
                size="xs"
                style={{ cursor: 'pointer' }}
              >
                {b}
              </Badge>
            ))}
            {overridden ? (
              <Badge variant="light" color="yellow" size="xs">
                {t('config.overridden')}
              </Badge>
            ) : (
              <Badge variant="light" color="gray" size="xs">
                {t('config.default')}
              </Badge>
            )}
          </Group>
          <Text c="dimmed" size="xs" mt={2}>
            {item.description}
          </Text>
          <Text c="dimmed" size="xs" mt={2}>
            {t('config.defaultValue', { value: JSON.stringify(item.defaultValue) })}
          </Text>
          {err && (
            <Text c="red" size="xs" mt={4}>
              {err}
            </Text>
          )}
        </div>

        <Stack gap={6} style={{ flexShrink: 0, width: item.type === 'json' ? 280 : 160 }}>
          {item.type === 'boolean' && (
            <Switch
              checked={Boolean(draft)}
              onChange={(e) => {
                const v = e.currentTarget.checked;
                setDraft(v);
                save(v); // toggles apply immediately
              }}
              label={t(draft ? 'config.on' : 'config.off')}
            />
          )}
          {item.type === 'number' && (
            <>
              {/* The bounds are shown, not just enforced. A field that silently
                  clamps tells you the value you typed was wrong only after you
                  have typed it; the range is what you needed before. */}
              <Text size="xs" c="dimmed" ta="right">
                {rangeLabel(item, t)}
              </Text>
              <NumberInput
                value={typeof draft === 'number' ? draft : Number(draft)}
                onChange={(v) => setDraft(typeof v === 'number' ? v : Number(v))}
                min={item.min ?? undefined}
                max={item.max ?? undefined}
                // Eight of the eleven numeric knobs are `.int()` in their schema
                // while the editor stepped by 0.05 with four decimals — so you
                // could type 17.5 into an hour-of-day field, press Save, and have
                // the API reject it. The editor now knows what the schema knows.
                step={item.integer ? 1 : 0.05}
                decimalScale={item.integer ? 0 : 4}
                allowDecimal={!item.integer}
                clampBehavior="strict"
              />
              <Group gap={6} justify="flex-end">
                {overridden && (
                  <Button size="xs" variant="subtle" color="gray" onClick={() => save(null)}>
                    {t('common.reset')}
                  </Button>
                )}
                <Button
                  size="xs"
                  disabled={!dirty}
                  loading={update.isPending}
                  onClick={() => save(draft)}
                >
                  {t('common.save')}
                </Button>
              </Group>
            </>
          )}
          {item.type === 'json' && (
            <>
              <JsonInput
                value={typeof draft === 'string' ? draft : JSON.stringify(draft, null, 2)}
                onChange={(v) => setDraft(v)}
                autosize
                minRows={3}
                formatOnBlur
                validationError={t('config.invalidJson')}
              />
              <Group gap={6} justify="flex-end">
                {overridden && (
                  <Button size="xs" variant="subtle" color="gray" onClick={() => save(null)}>
                    {t('common.reset')}
                  </Button>
                )}
                <Button
                  size="xs"
                  loading={update.isPending}
                  onClick={() => {
                    try {
                      save(typeof draft === 'string' ? JSON.parse(draft) : draft);
                    } catch {
                      setErr(t('config.invalidJson'));
                    }
                  }}
                >
                  {t('common.save')}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

export function ConfigScreen() {
  const t = useT();
  const { data, isLoading, isError, error } = useAppConfig();
  // `?key=` is how a business rule hands you the knob it names.
  const [params] = useSearchParams();
  const focusKey = params.get('key');

  if (isError)
    return (
      <Text c="red" size="sm">
        {errorMessage(error)}
      </Text>
    );
  if (isLoading || !data)
    return (
      <Text c="dimmed" size="sm">
        {t('common.loading')}
      </Text>
    );

  const items = data.items;
  const groups = [...new Set(items.map((i) => i.group))];

  return (
    <Stack gap="lg">
      <Group gap={6} align="center">
        <Text c="dimmed" size="sm">
          {t('config.intro')}
        </Text>
        <InfoTip label={t('config.infoTip')} />
      </Group>

      {groups.map((g) => (
        <div key={g}>
          <Text fw={700} mb="xs">
            {g}
          </Text>
          <Stack gap="sm">
            {items
              .filter((i) => i.group === g)
              .map((i) => (
                <ConfigRow key={i.key} item={i} highlight={i.key === focusKey} />
              ))}
          </Stack>
        </div>
      ))}
    </Stack>
  );
}
